// @ts-nocheck
// Speckle's ObjectClicked event/types are loosely typed; @ts-nocheck mirrors the
// pragmatic choice already made in speckle.filters.ts.
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { ViewerEvent } from "@speckle/viewer";

import { useSpeckleStore } from "@/stores/speckle/speckle.store";

// The raw Speckle model object attached to a clicked node (type/family/category/
// name/parameters/etc.). Shape varies by source model, so it's treated as a bag.
export type InspectedElement = Record<string, unknown> | null;

export const useSpeckleInspect = defineStore("speckleInspect", () => {
  const core = useSpeckleStore();

  const clickedElement = ref<InspectedElement>(null);
  // The world-tree object id of the raw-bearing node behind the last click. Same id space as
  // speckle.filters' indexes, so it works directly with SelectionExtension.selectObjects,
  // FilteringExtension.isolateObjects, and the camera's setCameraView. Lets the Inspect tool
  // highlight / zoom / isolate the exact clicked element.
  const clickedObjectId = ref<string | null>(null);
  const clickCount = ref(0);
  // The viewer instance our picking handler is currently bound to. Switching projects disposes the
  // viewer and builds a new one, so we track the instance (not a one-shot "registered" boolean) and
  // re-bind to each new viewer. A stale boolean here was exactly why the Inspect tool went dead after
  // a project switch: register() saw registered === true and never attached to the new viewer.
  let registeredViewer = null;

  const clear = () => {
    clickedElement.value = null;
    clickedObjectId.value = null;
  };

  // Resolve a click to the raw model data of the nearest ancestor node that carries it.
  const handleObjectClicked = (event) => {
    clickCount.value += 1;
    const hit = event?.hits?.[0];
    if (!hit) {
      clickedElement.value = null;
      clickedObjectId.value = null;
      return;
    }
    // Walk up to the first ancestor carrying raw model data.
    let node = hit.node;
    while (node && !(node.model && node.model.raw) && node.parent) node = node.parent;
    clickedElement.value = node?.model?.raw ?? null;
    clickedObjectId.value = node?.model?.id != null ? String(node.model.id) : node?.id != null ? String(node.id) : null;
  };

  // Bind the picking handler to a viewer instance (once per instance). Picking is enabled whenever a
  // model is loaded (the Inspect tool's isModelReady watcher owns the pick-guard's default "on" state),
  // so this fires for clicks in any tool; consumers gate on their own tool's active state. The old
  // viewer's listener dies with it on dispose, so there's nothing to detach here.
  const register = (v) => {
    if (!v || v === registeredViewer) return;
    v.on(ViewerEvent.ObjectClicked, handleObjectClicked);
    registeredViewer = v;
  };

  // Re-bind whenever the viewer instance changes — e.g. a project switch tears the viewer down and
  // builds a new one — and drop the stale selection so the panel doesn't carry the old project's click.
  watch(
    () => core.viewer,
    (v) => {
      clear();
      register(v);
    },
    { immediate: true }
  );

  return {
    clickedElement,
    clickedObjectId,
    clickCount,
    clear,
  };
});

export default useSpeckleInspect;
